import React, { useRef } from 'react';
import { TrendingUp, TrendingDown, ArrowRight, ArrowDown, Users, Clock } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Cell, LabelList } from 'recharts';
import styled, { useTheme } from 'styled-components';
import { motion } from 'framer-motion';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../ui/chart';

const LoadingSkeleton = styled.div`
  background: linear-gradient(90deg, ${props => props.theme.colors.backgroundTertiary} 25%, ${props => props.theme.colors.border} 50%, ${props => props.theme.colors.backgroundTertiary} 75%);
  background-size: 200% 100%;
  animation: pulse 1.5s ease-in-out infinite;
  border-radius: ${props => props.theme.borders.radius.md};
  height: 400px;
  width: 100%;

  @keyframes pulse {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  position: relative;

  /* Ajusta o card para ficar mais horizontal */
  [data-chart] {
    aspect-ratio: auto;
    height: 350px;
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    [data-chart] {
      height: 400px;
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    [data-chart] {
      height: 450px;
    }
  }
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[2]};
  margin-bottom: ${props => props.theme.spacing[4]};

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing[3]};
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(5, 1fr);
    gap: ${props => props.theme.spacing[4]};
    margin-bottom: ${props => props.theme.spacing[6]};
  }
`;

const StepCard = styled(motion.div)<{ $level: number }>`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[3]};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: pointer;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: ${props => props.theme.spacing[4]};
    border-radius: ${props => props.theme.borders.radius.xl};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => {
      const level = props.$level;
      const theme = props.theme;
      switch (level) {
        case 1: return theme.colors.info;
        case 2: return theme.colors.warning;
        case 3: return theme.colors.success;
        case 4: return theme.colors.error;
        case 5: return theme.colors.textTertiary;
        default: return theme.colors.primary;
      }
    }};
    opacity: 0.8;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => {
      const level = props.$level;
      const theme = props.theme;
      switch (level) {
        case 1: return theme.colors.info;
        case 2: return theme.colors.warning;
        case 3: return theme.colors.success;
        case 4: return theme.colors.error;
        case 5: return theme.colors.textTertiary;
        default: return theme.colors.primary;
      }
    }};

    &::before {
      height: 4px;
      opacity: 1;
    }
  }
`;

const StepIcon = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: ${props => `${props.$color}22`};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[2]};
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    width: 48px;
    height: 48px;
    margin-bottom: ${props => props.theme.spacing[3]};
  }

  svg {
    color: ${props => props.$color};
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  ${StepCard}:hover & {
    transform: scale(1.1) rotate(5deg);
    background: ${props => `${props.$color}33`};
    box-shadow: 0 0 20px ${props => `${props.$color}44`};
  }
`;

const StepValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[1]};
  letter-spacing: -0.02em;
  line-height: 1;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    margin-bottom: ${props => props.theme.spacing[2]};
  }
`;

const StepTitle = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[1]};
  text-transform: uppercase;
  letter-spacing: 0.03em;

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.sm};
    letter-spacing: 0.05em;
  }
`;

const StepDescription = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.3;
  display: none;

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    display: block;
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    line-height: 1.4;
  }
`;

const ConversionIndicator = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  margin-top: ${props => props.theme.spacing[2]};
  padding: ${props => `${props.theme.spacing[1]} ${props.theme.spacing[2]}`};
  border-radius: ${props => props.theme.borders.radius.full};
  background: ${props => props.$positive ? `${props.theme.colors.success}22` : `${props.theme.colors.error}22`};
  color: ${props => props.$positive ? props.theme.colors.success : props.theme.colors.error};
  font-size: 0.65rem;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.typography.fontSize.xs};
  }

  svg {
    width: 10px;
    height: 10px;

    @media (min-width: ${props => props.theme.breakpoints.sm}) {
      width: 12px;
      height: 12px;
    }
  }
`;

const EmptyStateContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing[12]} ${props => props.theme.spacing[6]};
  text-align: center;
  min-height: 400px;
`;

const EmptyStateIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15 0%, ${props => props.theme.colors.primary}08 100%);
  border: 2px solid ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      ${props => props.theme.colors.primary}10 50%,
      transparent 70%
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  svg {
    color: ${props => props.theme.colors.primary};
    position: relative;
    z-index: 1;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const EmptyStateDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  max-width: 400px;
  line-height: 1.6;
`;

const EmptyStateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};

  svg {
    width: 14px;
    height: 14px;
    color: ${props => props.theme.colors.primary};
  }
`;

interface FunnelStepData {
  id: string;
  title: string;
  description: string;
  value: number;
  percentage?: number;
  icon: React.ElementType;
  level: number;
}

interface ClassificationChartProps {
  steps: FunnelStepData[];
  loading?: boolean;
}


const ClassificationChart: React.FC<ClassificationChartProps> = ({ steps, loading = false }) => {
  const theme = useTheme();
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Funil de Classificação</CardTitle>
          <CardDescription>Distribuição de leads por etapa</CardDescription>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Cores por level
  const getColorByLevel = (level: number) => {
    switch (level) {
      case 1: return theme.colors.info;
      case 2: return theme.colors.warning;
      case 3: return theme.colors.success;
      case 4: return theme.colors.error;
      case 5: return theme.colors.textTertiary;
      default: return theme.colors.primary;
    }
  };

  // Transforma os dados para o formato do recharts
  const chartData = steps.map(step => ({
    name: step.title,
    value: step.value,
    fill: getColorByLevel(step.level),
  }));

  const chartConfig = steps.reduce((acc, step) => {
    acc[step.id] = {
      label: step.title,
      color: getColorByLevel(step.level),
    };
    return acc;
  }, {} as ChartConfig);

  // Calcula totais para o footer
  const totalLeads = steps[0]?.value || 0;
  const totalSales = steps.find(s => s.id === 'convertido')?.value || 0;
  const conversionRate = totalLeads > 0 ? ((totalSales / totalLeads) * 100).toFixed(1) : '0.0';

  // Calcula taxa de conversão entre etapas
  const calculateConversion = (currentIndex: number): number => {
    if (currentIndex === 0) return 0;
    const previous = steps[currentIndex - 1]?.value || 0;
    const current = steps[currentIndex]?.value || 0;
    if (previous === 0) return 0;
    return ((current / previous) * 100);
  };

  // Verifica se não há dados
  const hasNoData = !steps || steps.length === 0 || steps.every(step => step.value === 0);

  // Estado vazio
  if (hasNoData) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>Visualize o avanço dos leads em cada etapa do processo</CardDescription>
        </CardHeader>
        <CardContent>
          <EmptyStateContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <EmptyStateIcon>
              <Users size={56} />
            </EmptyStateIcon>
            <EmptyStateTitle>Nenhum lead ainda</EmptyStateTitle>
            <EmptyStateDescription>
              Seus leads aparecerão aqui assim que começarem a interagir com o bot.
              O funil mostrará o progresso deles em cada etapa do processo.
            </EmptyStateDescription>
          </EmptyStateContainer>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardDescription>Visualize o avanço dos leads em cada etapa do processo</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Cards de etapas */}
        <StepsContainer>
          {steps.map((step, index) => {
            const StepIconComponent = step.icon;
            const color = getColorByLevel(step.level);
            const conversion = calculateConversion(index);
            const isPositiveConversion = conversion >= 50;

            return (
              <StepCard
                key={step.id}
                $level={step.level}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: [0.34, 1.56, 0.64, 1]
                }}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
              >
                <StepIcon $color={color}>
                  <StepIconComponent size={20} />
                </StepIcon>
                <StepValue>{step.value.toLocaleString()}</StepValue>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>

                {index > 0 && (
                  <ConversionIndicator $positive={isPositiveConversion}>
                    {isPositiveConversion ? <TrendingUp /> : <TrendingDown />}
                    {conversion.toFixed(1)}%
                  </ConversionIndicator>
                )}
              </StepCard>
            );
          })}
        </StepsContainer>

        {/* Gráfico de barras */}
        <ChartWrapper ref={chartWrapperRef}>
          <ChartContainer config={chartConfig}>
            <BarChart
              accessibilityLayer
              data={chartData}
              layout="vertical"
              margin={{
                left: 0,
                right: 20,
                top: 10,
                bottom: 10,
              }}
            >
              <defs>
                {steps.map((step) => {
                  const color = getColorByLevel(step.level);
                  return (
                    <linearGradient key={step.id} id={`gradient-${step.id}`} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                      <stop offset="100%" stopColor={color} stopOpacity={1} />
                    </linearGradient>
                  );
                })}
              </defs>

              <CartesianGrid
                horizontal={false}
                strokeDasharray="3 3"
                stroke={theme.colors.border}
                opacity={0.2}
              />

              <YAxis
                dataKey="name"
                type="category"
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                width={90}
                tick={{
                  fill: theme.colors.textSecondary,
                  fontSize: 11,
                  fontWeight: 600
                }}
              />

              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                domain={[0, Math.max(...chartData.map(d => d.value), 1)]}
                ticks={[]}
                tick={false}
                tickFormatter={() => ''}
              />

              <ChartTooltip
                cursor={{ fill: theme.colors.border, opacity: 0.15 }}
                content={<ChartTooltipContent hideLabel indicator="line" />}
              />

              <Bar
                dataKey="value"
                radius={[0, 8, 8, 0]}
                animationDuration={800}
                animationEasing="ease-in-out"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#gradient-${steps[index].id})`}
                  />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  style={{
                    fill: theme.colors.textPrimary,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                  formatter={(value: number) => value.toLocaleString()}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </ChartWrapper>
      </CardContent>

      <CardFooter style={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing[2],
        fontSize: theme.typography.fontSize.xs,
        borderTop: `1px solid ${theme.colors.border}`,
        paddingTop: theme.spacing[3]
      }}>
        <div style={{
          display: 'flex',
          gap: theme.spacing[2],
          fontWeight: theme.typography.fontWeight.semibold,
          alignItems: 'center',
          color: theme.colors.textPrimary,
          fontSize: theme.typography.fontSize.sm
        }}>
          <TrendingUp
            style={{
              width: 16,
              height: 16,
              color: parseFloat(conversionRate) >= 10 ? theme.colors.success : theme.colors.warning
            }}
          />
          Taxa de conversão: {conversionRate}%
        </div>
        <div style={{
          color: theme.colors.textSecondary,
          fontSize: theme.typography.fontSize.xs
        }}>
          Total de {totalLeads.toLocaleString()} leads no período
        </div>
      </CardFooter>
    </Card>
  );
};

export default ClassificationChart;
